/**
 * ============================================================================
 * [P1-03] EditorPanel Component
 *
 * 단일 에디터 패널 컴포넌트 (재사용 가능)
 *
 * 기능:
 * - 제목/주제/출처 입력
 * - 본문 textarea
 * - 글자 수 카운터
 * - 진행률 바
 * - 지우기/저장/다운로드 버튼
 * - 유형 선택 (레퍼런스)
 *
 * Safety:
 * - Controlled Component 패턴 적용
 * - value와 onChange를 통한 상태 동기화
 * ============================================================================
 */

import type { ChangeEvent } from 'react'
import { ProgressBar } from './ProgressBar'
import { LLMValidationDropdown } from './LLMValidationDropdown'
import type { LLMServiceType } from '@/data/llm-prompts'
import styles from './EditorPanel.module.css'

// ============================================================================
// 타입 정의
// ============================================================================
interface EditorPanelProps {
  // 패널 정보
  type: 'edit' | 'reference'
  title: string
  icon: string

  // 콘텐츠 값
  content: string
  metaLabel: string       // 주제/출처 라벨
  metaValue: string       // 주제/출처 값
  metaPlaceholder: string // 주제/출처 placeholder

  // 설정
  charLimit: number
  placeholder?: string

  // 이벤트 핸들러
  onContentChange: (content: string) => void
  onMetaChange: (value: string) => void
  onClear: () => void
  onSave?: () => void
  onCopy?: () => void  // Copy 버튼 (TXT 다운로드 대체)
  onPost?: () => void  // 반자동 포스팅 (Threads)

  // LLM 검증 핸들러
  onLLMValidate?: (llm: LLMServiceType, panelType: 'edit' | 'reference') => void
  onAnalyzeReference?: () => void

  // 레퍼런스 전용 (선택적)
  refType?: 'structure' | 'idea' | 'unspecified'
  onRefTypeChange?: (type: 'structure' | 'idea' | 'unspecified') => void
}

// ============================================================================
// [P1-03] EditorPanel 컴포넌트
// ============================================================================
export function EditorPanel({
  type,
  title,
  icon,
  content,
  metaLabel,
  metaValue,
  charLimit,
  placeholder,
  onContentChange,
  onMetaChange,
  onClear,
  onSave,
  onCopy,
  onPost,
  onLLMValidate,
  onAnalyzeReference,
  refType,
  onRefTypeChange,
}: EditorPanelProps) {
  // ========================================================================
  // 이벤트 핸들러
  // ========================================================================
  const handleContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange(e.target.value)
  }

  const handleMetaChange = (e: ChangeEvent<HTMLInputElement>) => {
    onMetaChange(e.target.value)
  }

  // 글자 수 계산 (undefined 방어)
  const safeContent = content ?? ''
  const safeMetaValue = metaValue ?? ''
  const charCount = safeContent.length
  const isOverLimit = charCount > charLimit

  // ========================================================================
  // 렌더링
  // ========================================================================
  return (
    <div className={styles.editorPanel}>
      {/* ================================================================
          패널 헤더 (인라인 레이아웃)
          ================================================================ */}
      <div className={styles.panelHeaderInline}>
        <h3 className={styles.panelTitle}>
          <span className={styles.panelIcon}>{icon}</span>
          {title}
        </h3>
        
        {/* 메타 입력 (주제/출처) - 인라인 */}
        <div className={styles.metaInputInline}>
          <span className={styles.metaLabel}>{metaLabel} (선택사항)</span>
          <input
            type="text"
            className={styles.inputFieldInline}
            value={safeMetaValue}
            onChange={handleMetaChange}
            placeholder={`예: 마케팅, 개발일지`}
            aria-label={metaLabel}
          />
        </div>
        
        {/* 글자 수 카운터 - 인라인 */}
        <div className={styles.charCountInline}>
          <span className={charCount > charLimit ? styles.danger : ''}>{charCount}</span>
          <span className={styles.separator}>/</span>
          <span>{charLimit} 자</span>
        </div>
      </div>

      {/* ================================================================
          본문 텍스트 영역
          ================================================================ */}
      <div className={styles.textareaContainerFlex}>
        <textarea
          id={type === 'reference' ? 'ref-text-input' : 'edit-text-input'}
          className={`${styles.textarea} ${isOverLimit ? styles.overLimit : ''}`}
          value={safeContent}
          onChange={handleContentChange}
          placeholder={placeholder || '내용을 입력하세요...'}
          aria-label={title}
        />
      </div>

      {/* ================================================================
          진행률 바
          ================================================================ */}
      <ProgressBar current={charCount} limit={charLimit} />

      {/* ================================================================
          액션 버튼 그룹
          ================================================================ */}
      <div className={styles.actionButtonGroup}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.secondary}`}
          onClick={onClear}
          disabled={safeContent.length === 0 && safeMetaValue.length === 0}
        >
          지우기
        </button>
        {onSave && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.primary}`}
            onClick={onSave}
            disabled={safeContent.length === 0}
          >
            저장
          </button>
        )}
        {onCopy && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.secondary}`}
            onClick={onCopy}
            disabled={safeContent.length === 0}
          >
            Copy
          </button>
        )}
      </div>

      {/* ================================================================
          LLM 검증 + 유형 선택 행
          ================================================================ */}
      <div className={styles.advancedActionsRow}>
        <LLMValidationDropdown
          panelType={type}
          disabled={safeContent.length === 0}
          onValidate={onLLMValidate}
        />

        {type === 'reference' && onAnalyzeReference && (
          <button
            type="button"
            className={styles.btnReferenceAnalysis}
            onClick={onAnalyzeReference}
            disabled={safeContent.trim().length === 0}
            aria-label="가상 패널 분석"
          >
            가상 패널 분석
          </button>
        )}

        {/* 반자동 포스팅 버튼 (edit 패널 전용) */}
        {type === 'edit' && onPost && (
          <button
            type="button"
            className={styles.btnSemiAuto}
            onClick={onPost}
            disabled={safeContent.length === 0}
            aria-label="Threads에 반자동으로 포스팅하기"
          >
            🚀 반자동 포스팅
          </button>
        )}

        {/* 유형 선택 (레퍼런스 패널만) */}
        {type === 'reference' && onRefTypeChange && (
          <div className={styles.refTypeInline}>
            <span className={styles.refTypeLabel}>유형:</span>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name={`refType-${type}`}
                checked={refType === 'structure'}
                onChange={() => onRefTypeChange('structure')}
              />
              구조
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name={`refType-${type}`}
                checked={refType === 'idea'}
                onChange={() => onRefTypeChange('idea')}
              />
              아이디어
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// 기본 export
// ============================================================================
export default EditorPanel
